import {
  baseURLImage,
  getNameProfile,
  formatDateTimeWithAMPM,
  formatDateToAustralian,
  isDateBeforeToday,
} from "Utils/utils";
import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import "./style.css";
import { roleAccess } from "helpers/RolesPermission";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import InprogressShiftStatusPopover from "./InprogressShiftStatusPopover";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import {
  expandShiftsForDashboard,
  getScheduledShiftDisplay,
  workerFullName,
} from "./dashboardShiftUtils";

const ExpiringDateTable = ({
  title,
  item,
  isExpirDoc = false,
  onLoadMore,
  loadingMore = false,
  hasMore = false,
}) => {
  const handleScroll = useCallback(
    (e) => {
      if (!onLoadMore || loadingMore || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop - clientHeight < 80) {
        onLoadMore();
      }
    },
    [onLoadMore, loadingMore, hasMore]
  );

  const displayItems = useMemo(() => {
    const source = item || [];
    if (title !== "Scheduled Shift") {
      return source
        .filter((el) => el?.name && el?.worker)
        .map((el) => ({
          ...el,
          _rowKey: `doc-${el?.id}`,
          _isAdvance: false,
        }));
    }
    return expandShiftsForDashboard(source);
  }, [item, title]);

  const handleImageError = (e, fname, lname, useOpenPlaceholder) => {
    const isopenShift = title === "Scheduled Shift" && useOpenPlaceholder;
    return (e.target.src = getNameProfile(
      isopenShift ? "Open" : fname,
      isopenShift ? "Shift" : lname
    ));
  };

  const handleRedirection = (element) => {
    if (
      isExpirDoc &&
      roleAccess(
        [],
        PERMISSION_KEYS.HRM,
        PERMISSION_KEYS_CHILDREN.HRM.WORKER.SHOW
      )
    ) {
      return `/worker-profile/${element?.worker?.uuid}`;
    } else if (
      roleAccess(
        [],
        PERMISSION_KEYS.ROSTERING,
        PERMISSION_KEYS_CHILDREN.ROSTERING.SCHEDULE
      )
    ) {
      return `/rostering/schedule`;
    } else return "#";
  };

  return (
    <div>
      <div className='mb-3 sm:mb-4'>
        <div className='flex items-center gap-2'>
          <div className='w-1 h-5 sm:h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full'></div>
          <h3 className='text-base sm:text-lg font-bold text-gray-900'>
            {title}
          </h3>
        </div>
      </div>
      <div className='mt-3 sm:mt-4'>
        <div
          className='max-h-[350px] sm:max-h-[400px] lg:max-h-[450px] overflow-x-auto overflow-y-auto custom-scroll space-y-2'
          onScroll={onLoadMore ? handleScroll : undefined}
        >
          {displayItems && displayItems.length > 0 ? (
            displayItems.map((element) => {
              const isExpired =
                isExpirDoc && isDateBeforeToday(element?.expire_date);
              const isExpiringSoon = isExpirDoc && !isExpired;
              const needsAttention = isExpired || isExpiringSoon;
              const isScheduledShift = title === "Scheduled Shift";
              const display = isScheduledShift
                ? getScheduledShiftDisplay(element)
                : null;
              const displayTitle = isScheduledShift
                ? display.title
                : workerFullName(element?.worker);
              const useOpenAvatar = Boolean(display?.useOpenAvatar);
              const avatarWorker = element?.worker;

              return (
                <Link
                  to={handleRedirection(element)}
                  key={element?._rowKey || element?.id}
                  className='block'
                >
                  <div
                    className={`
                    bg-white border rounded-lg p-4 
                    hover:shadow-md transition-all duration-200 group
                    ${
                      needsAttention
                        ? "border-red-300 bg-red-50/50 hover:border-red-400"
                        : "border-gray-200 hover:border-blue-300"
                    }
                  `}
                  >
                    <div className='flex items-center justify-between gap-4'>
                      <div className='flex items-center flex-1 min-w-0'>
                        <div className='relative h-12 w-12 flex-shrink-0'>
                          <img
                            onError={(e) =>
                              handleImageError(
                                e,
                                avatarWorker?.first_name,
                                avatarWorker?.last_name,
                                useOpenAvatar
                              )
                            }
                            className='object-cover h-12 w-12 rounded-full ring-2 ring-gray-200 group-hover:ring-blue-400 transition-all'
                            src={
                              useOpenAvatar
                                ? getNameProfile("Open", "Shift")
                                : avatarWorker?.photo
                                ? baseURLImage(avatarWorker?.photo)
                                : getNameProfile(
                                    avatarWorker?.first_name || "",
                                    avatarWorker?.last_name || ""
                                  )
                            }
                            alt=''
                          />
                          {!isExpirDoc &&
                            (element?.worker_start_time ||
                              String(element?.progress || "").toLowerCase() ===
                                "started") && (
                            <InprogressShiftStatusPopover
                              workerStartTime={
                                element?.worker_start_time || element?.start_time
                              }
                              startTime={element?.start_time}
                            />
                          )}
                        </div>
                        <div className='ml-4 flex-1 min-w-0'>
                          <div className='flex items-center gap-2 min-w-0 flex-wrap'>
                            <div
                              className='font-semibold text-sm text-gray-900 capitalize truncate group-hover:text-blue-600 transition-colors'
                              title={displayTitle}
                            >
                              {displayTitle}
                            </div>
                            {isScheduledShift && display?.isAdvance && (
                              <span className='shrink-0 inline-flex items-center px-1.5 py-px text-[9px] font-semibold tracking-wide uppercase rounded bg-sky-100 text-sky-800 border border-sky-300 leading-tight'>
                                Advance Shift
                              </span>
                            )}
                            {isScheduledShift && display?.progressLabel && (
                              <span className='shrink-0 inline-flex items-center px-1.5 py-px text-[9px] font-semibold tracking-wide uppercase rounded bg-amber-100 text-amber-800 border border-amber-300 leading-tight'>
                                {display.progressLabel}
                              </span>
                            )}
                          </div>
                          <div className='mt-1 text-xs text-gray-600 truncate'>
                            {isExpirDoc ? (
                              element?.name
                            ) : element?.participants?.length > 1 ? (
                              <AvatarGroup
                                popoverCss='flex w-[100%]'
                                arrayData={element?.participants}
                                cssClass='mt-0'
                                maxLength={4}
                              />
                            ) : (
                              `${
                                element?.participants?.[0]?.first_name || ""
                              } ${
                                element?.participants?.[0]?.last_name || ""
                              }`.trim()
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='flex-shrink-0 text-right'>
                        <div
                          className={`
                          text-sm font-semibold
                          ${needsAttention ? "text-red-600" : "text-gray-700"}
                        `}
                        >
                          {isExpirDoc
                            ? formatDateToAustralian(element?.expire_date)
                            : formatDateTimeWithAMPM(element?.start_time)}
                        </div>
                        {isExpired && (
                          <div className='text-xs text-red-500 mt-1 font-medium'>
                            Expired
                          </div>
                        )}
                        {isExpiringSoon && (
                          <div className='text-xs text-red-500 mt-1 font-medium'>
                            Expiring Soon
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })
          ) : (
            <div className='bg-white border border-gray-200 rounded-lg p-8 text-center'>
              <p className='text-gray-500'>
                {title === "Scheduled Shift"
                  ? "No upcoming shifts available."
                  : title === "Expiring Docs"
                  ? "No expiring documents available."
                  : "No data available"}
              </p>
            </div>
          )}
          {onLoadMore && loadingMore && (
            <div className='py-3 text-center text-sm text-gray-500'>
              Loading more...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpiringDateTable;
